import { type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

type Props = {
	id: string
	title: string
	className: string
	children: ReactNode
	onHeightChange?(height: number): void
}
const LandingPagePreviewFrame: FC<Props> = ({ id, title, className, children, onHeightChange }) => {
	const iframeRef = useRef<HTMLIFrameElement>(null)
	const lastHeight = useRef(0)
	const [mountNode, setMountNode] = useState<HTMLElement | null>(null)

	const updateFrameHeight = useCallback(() => {
		const iframeDocument = iframeRef.current?.contentDocument
		const iframe = iframeRef.current
		if (!iframeDocument || !iframe) return

		const height = iframeDocument.getElementById("landing-page-preview-root")?.scrollHeight ?? 0
		if (height === lastHeight.current) return
		lastHeight.current = height
		iframe.style.height = `${height}px`
		onHeightChange?.(height)
	}, [onHeightChange])

	const setupFrame = useCallback(() => {
		const iframeDocument = iframeRef.current?.contentDocument
		// Head is not nullable, but HMR can cause the document to be in a weird state where head is missing, so we need to check for it just in case
		if (!iframeDocument || !iframeDocument.head) return

		const head = iframeDocument.head
		head.replaceChildren()

		const base = iframeDocument.createElement("base")
		base.href = `${window.location.origin}/`
		head.append(base)

		for (const node of document.querySelectorAll('link[rel="stylesheet"], style')) {
			head.append(node.cloneNode(true))
		}

		const frameStyles = iframeDocument.createElement("style")
		frameStyles.textContent = `
			html {
				background: white;
			}

			body {
				margin: 0;
				min-width: 0;
				background: white;
				overflow: hidden;
			}

			#landing-page-preview-root {
				display: flow-root;
			}
		`
		head.append(frameStyles)

		setMountNode(iframeDocument.getElementById("landing-page-preview-root"))
		requestAnimationFrame(updateFrameHeight)
	}, [updateFrameHeight])

	useEffect(() => {
		setupFrame()
	}, [setupFrame])

	useLayoutEffect(() => {
		if (!mountNode) return
		updateFrameHeight()
	})

	useEffect(() => {
		if (!mountNode) return

		const observer = new ResizeObserver(updateFrameHeight)
		observer.observe(mountNode)
		observer.observe(mountNode.ownerDocument.body)
		updateFrameHeight()
		window.addEventListener("resize", updateFrameHeight)

		return () => {
			observer.disconnect()
			window.removeEventListener("resize", updateFrameHeight)
		}
	}, [mountNode, updateFrameHeight])

	return (
		<>
			<iframe
				id={id}
				ref={iframeRef}
				title={title}
				className={className}
				scrolling="no"
				srcDoc="<!doctype html><html><head></head><body><div id=&quot;landing-page-preview-root&quot;></div></body></html>"
				onLoad={setupFrame}
			/>
			{mountNode && createPortal(children, mountNode)}
		</>
	)
}

export default LandingPagePreviewFrame
